'use client'
import React from 'react'
import * as api from '../../app/axios/api.jsx'
import { toast } from 'sonner'
import { useState, useEffect } from 'react'
import {
  Search,
  Trash2,
  FileText,
  User,
  Calendar,
  Book,
  Hash,
  Building,
  AlertTriangle,
  X,
} from 'lucide-react'

const Page = () => {
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [certificateSearch, setCertificateSearch] = useState('')
  const [id, setId] = useState('')
  const [selectedCourse, setSelectedCourse] = useState('')

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchAstudent = async (searchId) => {
    try {
      setLoading(true)
      const res = await api.fetchAllCtrById(searchId)

      // Clear the id state after successful fetch
      setId('')
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Failed to fetch students data')
    } finally {
      setLoading(false)
    }
  }

  const openDeleteModal = (record) => {
    setRecordToDelete(record)
    setShowDeleteModal(true)
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setRecordToDelete(null)
  }

  const confirmDelete = async () => {
    if (!recordToDelete) return

    try {
      setDeleteLoading(true)
      await api.deleteCtr(recordToDelete.id)

      const updatedData = data.filter((item) => item.id !== recordToDelete.id)
      setData(updatedData)
      setFilteredData(updatedData)
      toast.success('Record deleted successfully')
      setShowDeleteModal(false)
      setRecordToDelete(null)
    } catch (error) {
      console.error('Error deleting record:', error)
      toast.error('Failed to delete record')
    } finally {
      setDeleteLoading(false)
    }
  }

  const fetchAll = async () => {
    try {
      setLoading(true)
      const res = await api.fetchAllCtr()
      setData(res.data)
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Failed to fetch students data')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    applyFilters()
  }

  const handleCertificateSearch = () => {
    applyFilters()
  }

  const handleCourseFilter = (course) => {
    setSelectedCourse(course)
    // Filter will be applied through useEffect
  }

  const applyFilters = () => {
    let filtered = [...data]

    // Apply name/enrollment search
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (item) =>
          item.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.enrollment_number
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
    }

    // Apply certificate search
    if (certificateSearch.trim()) {
      filtered = filtered.filter((item) =>
        item.certificate_id
          .toLowerCase()
          .includes(certificateSearch.toLowerCase())
      )
    }

    // Apply course filter
    if (selectedCourse && selectedCourse !== 'all') {
      filtered = filtered.filter(
        (item) => item.course.toLowerCase() === selectedCourse.toLowerCase()
      )
    }

    setFilteredData(filtered)
  }

  const clearSearch = () => {
    setSearchTerm('')
    setCertificateSearch('')
    setId('')
    setSelectedCourse('')
    setFilteredData(data)
  }

  // Get unique courses for filter dropdown
  const getUniqueCourses = () => {
    const courses = [...new Set(data.map((item) => item.course.toLowerCase()))]
    return courses.sort()
  }

  useEffect(() => {
    if (!searchTerm && !certificateSearch && !selectedCourse) {
      setFilteredData(data)
    } else {
      applyFilters()
    }
  }, [data, selectedCourse])

  useEffect(() => {
    fetchAll()
  }, [])

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6'>
          <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6'>
            Student Data Management
          </h1>

          {/* Search Section */}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4'>
            {/* Name/Enrollment Search */}
            <div className='space-y-2'>
              <label className='text-sm font-medium text-gray-700'>
                Search by Name or Enrollment Number
              </label>
              <div className='flex flex-col sm:flex-row gap-2'>
                <div className='relative flex-1'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
                  <input
                    type='text'
                    placeholder='Enter name or enrollment number...'
                    className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm whitespace-nowrap'
                >
                  Search
                </button>
              </div>
            </div>

            {/* Certificate Search */}
            <div className='space-y-2'>
              <label className='text-sm font-medium text-gray-700'>
                Search by Certificate ID
              </label>
              <div className='flex flex-col sm:flex-row gap-2'>
                <div className='relative flex-1'>
                  <FileText className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
                  <input
                    type='text'
                    placeholder='Enter certificate ID...'
                    className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm'
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchAstudent(id)}
                  />
                </div>
                <button
                  onClick={() => fetchAstudent(id)}
                  className='px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm whitespace-nowrap'
                >
                  Find
                </button>
              </div>
            </div>

            {/* Course Filter */}
            <div className='space-y-2'>
              <label className='text-sm font-medium text-gray-700'>
                Filter by Course
              </label>
              <div className='relative'>
                <Book className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
                <select
                  value={selectedCourse}
                  onChange={(e) => handleCourseFilter(e.target.value)}
                  className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white'
                >
                  <option value=''>All Courses</option>
                  {getUniqueCourses().map((course) => (
                    <option key={course} value={course}>
                      {course.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Clear Search Button */}
          <button
            onClick={clearSearch}
            className='px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm'
          >
            Clear All Searches
          </button>
        </div>

        {/* Results Count and Active Filters */}
        <div className='mb-4 px-2'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
            <p className='text-gray-600 text-sm sm:text-base'>
              Showing {filteredData.length} of {data.length} records
            </p>

            {/* Active Filters Display */}
            {(searchTerm || certificateSearch || selectedCourse) && (
              <div className='flex flex-wrap gap-2 items-center'>
                <span className='text-xs text-gray-500'>Active filters:</span>
                {searchTerm && (
                  <span className='px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1'>
                    Name/Enroll: {searchTerm}
                    <button
                      onClick={() => {
                        setSearchTerm('')
                        applyFilters()
                      }}
                      className='text-blue-600 hover:text-blue-800'
                    >
                      ×
                    </button>
                  </span>
                )}
                {certificateSearch && (
                  <span className='px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1'>
                    Certificate: {certificateSearch}
                    <button
                      onClick={() => {
                        setCertificateSearch('')
                        applyFilters()
                      }}
                      className='text-green-600 hover:text-green-800'
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedCourse && (
                  <span className='px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1'>
                    Course: {selectedCourse.toUpperCase()}
                    <button
                      onClick={() => {
                        setSelectedCourse('')
                        applyFilters()
                      }}
                      className='text-purple-600 hover:text-purple-800'
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Data Grid */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'>
          {filteredData.map((item) => (
            <div
              key={item.id}
              className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow'
            >
              {/* Header with Delete Button */}
              <div className='flex justify-between items-start mb-4'>
                <div className='flex items-center gap-2 min-w-0 flex-1'>
                  <User className='h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0' />
                  <h3 className='text-base sm:text-lg font-semibold text-gray-900 capitalize truncate'>
                    {item.student_name}
                  </h3>
                </div>
                <button
                  onClick={() => openDeleteModal(item)}
                  className='p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors flex-shrink-0'
                  title='Delete Record'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>

              {/* Student Details */}
              <div className='space-y-3'>
                <div className='flex items-center gap-2 text-xs sm:text-sm'>
                  <Hash className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0' />
                  <span className='text-gray-600 flex-shrink-0'>
                    Student ID:
                  </span>
                  <span className='font-medium truncate'>
                    {item.student_id}
                  </span>
                </div>

                <div className='flex items-center gap-2 text-xs sm:text-sm'>
                  <Hash className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0' />
                  <span className='text-gray-600 flex-shrink-0'>
                    Enrollment:
                  </span>
                  <span className='font-medium truncate'>
                    {item.enrollment_number}
                  </span>
                </div>

                <div className='flex items-center gap-2 text-xs sm:text-sm'>
                  <FileText className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0' />
                  <span className='text-gray-600 flex-shrink-0'>
                    Certificate ID:
                  </span>
                  <span className='font-medium text-blue-600 truncate'>
                    {item.certificate_id}
                  </span>
                </div>

                <div className='flex items-start gap-2 text-xs sm:text-sm'>
                  <Building className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0 mt-0.5' />
                  <span className='text-gray-600 flex-shrink-0'>College:</span>
                  <span className='font-medium break-words'>
                    {item.college_name}
                  </span>
                </div>

                <div className='flex items-center gap-2 text-xs sm:text-sm'>
                  <Book className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0' />
                  <span className='text-gray-600 flex-shrink-0'>Course:</span>
                  <span className='font-medium uppercase truncate'>
                    {item.course}
                  </span>
                </div>

                <div className='flex items-center gap-2 text-xs sm:text-sm'>
                  <Calendar className='h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0' />
                  <span className='text-gray-600 flex-shrink-0'>
                    Joining Date:
                  </span>
                  <span className='font-medium'>{item.joining_date}</span>
                </div>

                <div className='bg-gray-50 rounded-md p-3 mt-4'>
                  <span className='text-xs sm:text-sm text-gray-600'>
                    Technologies:
                  </span>
                  <div className='flex flex-wrap gap-1 mt-1'>
                    {item.technology.split(',').map((tech, index) => (
                      <span
                        key={index}
                        className='px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full'
                      >
                        {tech.trim()}
                      </span>
                    ))}
                  </div>
                </div>

                <div className='border-t pt-3 mt-4'>
                  <span className='text-xs text-gray-500'>Authorized by:</span>
                  <p className='text-xs sm:text-sm font-medium text-gray-700 break-words'>
                    {item.authorized_signature}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredData.length === 0 && (
          <div className='text-center py-12'>
            <div className='text-gray-400 mb-4'>
              <Search className='h-12 w-12 mx-auto' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No records found
            </h3>
            <p className='text-gray-500'>Try adjusting your search criteria</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-4 shadow-xl'>
              {/* Modal Header */}
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <AlertTriangle className='h-4 w-4 sm:h-5 sm:w-5 text-red-600' />
                  </div>
                  <h3 className='text-base sm:text-lg font-semibold text-gray-900'>
                    Confirm Delete
                  </h3>
                </div>
                <button
                  onClick={cancelDelete}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0'
                >
                  <X className='h-4 w-4 text-gray-500' />
                </button>
              </div>

              {/* Modal Content */}
              <div className='mb-6'>
                <p className='text-sm sm:text-base text-gray-600 mb-3'>
                  Are you sure you want to delete this student record? This
                  action cannot be undone.
                </p>

                {recordToDelete && (
                  <div className='bg-gray-50 rounded-md p-3'>
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Student: {recordToDelete.student_name}
                    </p>
                    <p className='text-xs text-gray-600'>
                      ID: {recordToDelete.student_id} | Enrollment:{' '}
                      {recordToDelete.enrollment_number}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className='flex flex-col sm:flex-row gap-3 sm:justify-end'>
                <button
                  onClick={cancelDelete}
                  className='px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors order-2 sm:order-1'
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className='px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2'
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                  ) : (
                    <Trash2 className='h-4 w-4' />
                  )}
                  Delete Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Page
